import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useAuth } from "@/auth/useAuth";
import { useBranch } from "@/auth/BranchContext";
import { fetchBranches } from "@/api/branches";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SIDEBAR_WIDTH } from "./Sidebar";
import { useThemeMode } from "@/theme-mode";

function initials(fullName: string): string {
  return fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

interface TopbarProps {
  onMenuClick: () => void;
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const { user, logout, hasPermission } = useAuth();
  const { selectedBranchId } = useBranch();
  const { t } = useTranslation();
  const canAllBranches = hasPermission("branch.all") && !user?.branchId;
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches, enabled: canAllBranches });
  const branchSubtitle = user?.branchId
    ? branches?.find((b) => b.id === user.branchId)?.name ?? t("common.branchAssigned")
    : selectedBranchId === "all"
      ? t("common.allBranches")
      : branches?.find((b) => b.id === selectedBranchId)?.name ?? t("common.branchAssigned");
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { mode, toggleMode } = useThemeMode();

  async function handleLogout() {
    setAnchorEl(null);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <AppBar
      className="crm-topbar"
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", minHeight: "62px !important", px: { xs: 1.5, md: 2.75 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton aria-label="Открыть меню" onClick={onMenuClick} sx={{ display: { md: "none" } }}>
            <MenuIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {branchSubtitle}
            </Typography>
            <Typography color="text.secondary" fontSize={12}>Учебный центр · CRM</Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton aria-label="Сменить тему" onClick={toggleMode}>
            {mode === "light" ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </IconButton>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer" }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: "var(--brand)", borderRadius: "9px" }}>
              {user ? initials(user.fullName) : "?"}
            </Avatar>
            <Typography variant="body2" fontWeight={600} sx={{ display: { xs: "none", sm: "block" } }}>
              {user?.fullName}
            </Typography>
          </Box>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t("common.logout")}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
